import { useState, useCallback } from "react"
import { useTranslation } from "react-i18next"
import { useParams } from "react-router"
import Papa from "papaparse"

import { EVAL_CASE_LIMIT } from "@/constants"
import {
  useUploadEvaluationCasesCsv,
  useListEvaluationCasesApi,
} from "@/hooks/api/eval"
import {
  getApiErrorPayloadMessage,
  getUserFacingApiErrorMessage,
} from "@/lib/apiErrors"

import { CsvRowData } from "../CsvPreviewModal"

interface UseCsvUploadReturn {
  showCsvDialog: boolean
  csvPreviewRows: CsvRowData[]
  csvPreviewErrors: string[]
  csvFile: File | null
  csvUploading: boolean
  handleOpenCsvDialog: () => void
  handleUploadCSV: (file: File) => void
  handleConfirmCsvUpload: () => Promise<void>
  handleCancelCsvPreview: () => void
  csvWouldExceedLimit: boolean
}

const REQUIRED_COLUMNS = ["input", "expected_output"] as const
const OPTIONAL_COLUMNS = ["note", "rubric"] as const
type CanonicalColumn =
  | (typeof REQUIRED_COLUMNS)[number]
  | (typeof OPTIONAL_COLUMNS)[number]

const normalizeCsvHeader = (value: string): string =>
  value
    .replace(/^\ufeff/, "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "")

const NORMALIZED_TO_CANONICAL: Record<string, CanonicalColumn> =
  Object.fromEntries(
    [...REQUIRED_COLUMNS, ...OPTIONAL_COLUMNS].map((c) => [
      normalizeCsvHeader(c),
      c,
    ])
  )

export const useCsvUpload = (
  casesLength: number,
  fetchCases: ReturnType<typeof useListEvaluationCasesApi>["fetchFn"],
  selectedEvaluatorId?: string,
  getCaseSearchParams?: () => URLSearchParams
): UseCsvUploadReturn => {
  const { t } = useTranslation()
  const { workspaceId, agentId } = useParams()

  const [showCsvDialog, setShowCsvDialog] = useState(false)
  const [csvPreviewRows, setCsvPreviewRows] = useState<CsvRowData[]>([])
  const [csvPreviewErrors, setCsvPreviewErrors] = useState<string[]>([])
  const [csvFile, setCsvFile] = useState<File | null>(null)
  const [csvUploading, setCsvUploading] = useState(false)

  const uploadEvaluationCasesCsv = useUploadEvaluationCasesCsv()

  const csvWouldExceedLimit =
    casesLength + csvPreviewRows.length > EVAL_CASE_LIMIT

  const resetCsvState = useCallback(() => {
    setCsvFile(null)
    setCsvPreviewRows([])
    setCsvPreviewErrors([])
  }, [])

  const handleOpenCsvDialog = useCallback(() => {
    resetCsvState()
    setShowCsvDialog(true)
  }, [resetCsvState])

  const handleUploadCSV = useCallback(
    (file: File) => {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (header) =>
          NORMALIZED_TO_CANONICAL[normalizeCsvHeader(header)] ?? header,
        complete: (results) => {
          const rows = results.data as CsvRowData[]
          const errors: string[] = []
          const fields = results.meta.fields || []
          const missingRequired = REQUIRED_COLUMNS.filter(
            (f) => !fields.includes(f)
          )
          if (missingRequired.length > 0) {
            errors.push(
              t("evaluation.evaluationTable.csvPreview.errors.missingColumns", {
                requiredColumns: REQUIRED_COLUMNS.join(", "),
                optionalColumns: OPTIONAL_COLUMNS.join(", "),
              })
            )
          }
          const MAX_ROW_ERRORS = 10
          const missingInputRows: number[] = []
          rows.forEach((row, idx) => {
            if (!row.input || row.input.trim() === "") {
              missingInputRows.push(idx + 2)
            }
          })
          missingInputRows.slice(0, MAX_ROW_ERRORS).forEach((rowNum) =>
            errors.push(
              t("evaluation.evaluationTable.csvPreview.errors.inputRequired", {
                row: rowNum,
              })
            )
          )
          if (missingInputRows.length > MAX_ROW_ERRORS) {
            errors.push(
              t(
                "evaluation.evaluationTable.csvPreview.errors.moreMissingInput",
                {
                  count: missingInputRows.length - MAX_ROW_ERRORS,
                }
              )
            )
          }
          const hasRubricValue = rows.some(
            (row) => (row.rubric ?? "").trim() !== ""
          )
          if (hasRubricValue && !selectedEvaluatorId) {
            errors.push(
              t(
                "evaluation.evaluationTable.csvPreview.errors.rubricWithoutTester"
              )
            )
          }
          setCsvPreviewRows(rows)
          setCsvPreviewErrors(errors)
          setCsvFile(file)
          setShowCsvDialog(true)
        },
        error: (err) => {
          setCsvPreviewRows([])
          setCsvPreviewErrors([
            t("evaluation.evaluationTable.csvPreview.errors.parseFailed", {
              message: err.message,
            }),
          ])
          setCsvFile(file)
          setShowCsvDialog(true)
        },
      })
    },
    [selectedEvaluatorId, t]
  )

  const handleConfirmCsvUpload = useCallback(async () => {
    if (!csvFile || !agentId || !workspaceId) return
    setCsvUploading(true)
    setCsvPreviewErrors([])
    try {
      const resp = await uploadEvaluationCasesCsv({
        file: csvFile,
        agent_id: agentId,
        wid: workspaceId,
        evaluator_id: selectedEvaluatorId,
      })
      const data = await resp.json()
      if (!resp.ok || data.error) {
        setCsvPreviewErrors([
          getApiErrorPayloadMessage(
            data,
            t,
            data.message ||
              data.error ||
              t("evaluation.evaluationTable.csvPreview.errors.uploadFailed")
          ),
        ])
        setCsvUploading(false)
        return
      }
      setShowCsvDialog(false)
      resetCsvState()
      fetchCases({
        searchParams:
          getCaseSearchParams?.() ||
          new URLSearchParams({ wid: workspaceId || "" }),
      })
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : t("evaluation.evaluationTable.csvPreview.errors.uploadFailed")
      setCsvPreviewErrors([getUserFacingApiErrorMessage(err, t, errorMessage)])
    } finally {
      setCsvUploading(false)
    }
  }, [
    csvFile,
    agentId,
    workspaceId,
    selectedEvaluatorId,
    uploadEvaluationCasesCsv,
    fetchCases,
    getCaseSearchParams,
    resetCsvState,
    t,
  ])

  const handleCancelCsvPreview = useCallback(() => {
    setShowCsvDialog(false)
    resetCsvState()
  }, [resetCsvState])

  return {
    showCsvDialog,
    csvPreviewRows,
    csvPreviewErrors,
    csvFile,
    csvUploading,
    handleOpenCsvDialog,
    handleUploadCSV,
    handleConfirmCsvUpload,
    handleCancelCsvPreview,
    csvWouldExceedLimit,
  }
}
